// ═══════════════════════════════════════════════════════════════════════════════
// HomeHubShell.jsx
//
// Drop-in replacement for the `<ExperimentWorkspace />` line in HomeHub.jsx
//
// Usage in HomeHub.jsx:
//   import HomeHubShell from './HomeHubShell';
//
//   // In the HomeHub export default render:
//   // Change: <ExperimentWorkspace />
//   // To:
//   // <HomeHubShell
//   //   authDisplayName={authDisplayName}
//   //   authUser={authUser}
//   //   isPremium={isPremium}
//   //   usageSummary={usageSummary}
//   //   onNavigate={onNavigate}
//   //   onAuthOpen={onAuthOpen}
//   //   onUpgrade={onUpgrade}
//   // />
//
// Required prop additions to HomeHub export default function signature:
//   authUser, isPremium, usageSummary, onNavigate, onUpgrade
//
// Required App.jsx additions to HomeHub JSX call:
//   authUser={authUser}
//   isPremium={isPremiumAccount}
//   usageSummary={usageSummary}
//   onNavigate={(id) => setActiveModule(id)}
//   onUpgrade={() => setMembershipOpen(true)}
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { experiments as expApi } from '../api/client';
import MyWorkstation     from './MyWorkstation';
import PublicExperiments from './PublicExperiments';

const C = {
  void:'#020508', deep:'#060b14', b0:'#0f1a2e', b1:'#1e293b',
  t1:'#e2e8f0', t3:'#8396b3', t4:'#627795',
  cyan:'#22d3ee', violet:'#a78bfa', green:'#4ade80',
  mono:"'JetBrains Mono',monospace",
};

const TABS = [
  { id:'workstation', label:'MY WORKSTATION',    icon:'⬡' },
  { id:'experiments', label:'PUBLIC EXPERIMENTS', icon:'◈' },
  // Future tabs — uncomment when ready:
  // { id:'feed',     label:'SOCIAL FEED',        icon:'◎' },
  // { id:'news',     label:'RESEARCH NEWS',       icon:'▣' },
];

export default function HomeHubShell({
  authDisplayName,
  authUser,
  isAuthenticated,
  isPremium,
  usageSummary,
  onNavigate,
  onAuthOpen,
  onUpgrade,
}) {
  const [tab, setTab] = useState('workstation');
  const [publicFeed, setPublicFeed] = useState(null);
  const [publicFeedError, setPublicFeedError] = useState('');

  useEffect(() => {
    let cancelled = false;

    expApi.publicFeed({ limit: 50 })
      .then((data) => {
        if (cancelled) return;
        setPublicFeed(Array.isArray(data?.experiments) ? data.experiments : []);
      })
      .catch((error) => {
        if (cancelled) return;
        setPublicFeedError(error?.data?.error || error?.message || 'Failed to load public experiments');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={{
      display:'flex', flexDirection:'column',
      height:'100%', minHeight:0, overflow:'hidden',
    }}>
      <style>{`
        @keyframes livePulse { 0%,100%{opacity:.5;} 50%{opacity:1;} }
        @keyframes wsPulse   { 0%,100%{opacity:.4;} 50%{opacity:.7;} }
      `}</style>

      {/* ── TAB BAR ── */}
      <div style={{
        display:'flex', alignItems:'stretch',
        background:C.deep, borderBottom:`1px solid ${C.b1}`,
        flexShrink:0, overflowX:'auto',
      }}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display:'flex', alignItems:'center', gap:7,
              padding:'12px 20px', background:'transparent', border:'none',
              borderBottom:`2px solid ${active ? C.cyan : 'transparent'}`,
              color: active ? C.cyan : C.t4,
              fontFamily:C.mono, fontSize:9, letterSpacing:'.1em',
              fontWeight: active ? 700 : 400,
              cursor:'pointer', transition:'all .15s', flexShrink:0,
            }}>
              <span style={{ fontSize:12 }}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          );
        })}

        {/* Live indicator */}
        <div style={{
          marginLeft:'auto', display:'flex', alignItems:'center',
          gap:5, padding:'0 16px', flexShrink:0,
          borderLeft:`1px solid ${C.b1}`,
        }}>
          <div style={{
            width:6, height:6, borderRadius:'50%', background:C.green,
            animation:'livePulse 1.8s ease-in-out infinite',
          }}/>
          <span style={{ fontFamily:C.mono, fontSize:8, color:C.green, letterSpacing:'.12em' }}>
            LIVE
          </span>
        </div>
      </div>

      {/* ── TAB CONTENT ── */}
      <div style={{ flex:1, overflowY:'auto', overflowX:'hidden' }}>
        {tab === 'workstation' && (
          <MyWorkstation
            authDisplayName={authDisplayName}
            authUser={authUser}
            isPremium={isPremium}
            usageSummary={usageSummary}
            onNavigate={onNavigate}
            onUpgrade={onUpgrade}
          />
        )}
        {tab === 'experiments' && (
          <PublicExperiments
            isAuthenticated={isAuthenticated}
            onNavigate={onNavigate}
            onAuthOpen={onAuthOpen}
            preloadedFeed={publicFeed}
            preloadedFeedError={publicFeedError}
          />
        )}
      </div>
    </div>
  );
}
